import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { Shop, ShopAttribute } from '../chat/types.js';
import { sanitizeUrl, dispatchChatMarianaLinks } from '../../utils/chat-utils.js';
import styles from '../../styles/mariana-shop-detail-inline.css?inline';
import {
  getPostalAdminLine,
  getFullScheduleLines,
  getDistanceLabel,
  renderShopStatusTag,
  isOriginalColorAmenity,
  getBrandsWithLogo,
  getShopImage,
} from './shop-detail-utils.js';

/**
 * Desktop-only inline shop detail view: replaces the shop cards carousel in
 * place (same message, no overlay/modal) when "Mais informações" is clicked.
 * Mobile keeps using mariana-shop-detail-sheet instead.
 *
 * Uses the chat's own scroll (.message-list) — no local scroll box. "Mais
 * informações" sticks to the top of the chat as usual. "Obter direções"
 * sticks to the bottom, but is wrapped (with the sections) in
 * .inline-footer-wrapper so its sticky position can never rise past that
 * wrapper's own top edge, i.e. never past the divider right after the hours.
 */
@customElement('mariana-shop-detail-inline')
export class MarianaShopDetailInline extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Object })
  shop: Shop | null = null;

  @property({ type: String })
  source = '';

  private get isAppCC(): boolean {
    return this.source.toLowerCase() === 'appcc';
  }

  private readonly handleBackClick = (): void => {
    this.dispatchEvent(new CustomEvent('inline-back', { bubbles: true, composed: true }));
  };

  private readonly handleImageClick = (): void => {
    if (!this.shop) return;
    const pageUrl = this.shop.page_url;
    if (!pageUrl) return;

    if (this.isAppCC) {
      dispatchChatMarianaLinks(pageUrl);
      return;
    }

    window.open(pageUrl, '_blank');
  };

  private readonly handleDirectionsClick = (): void => {
    if (!this.shop) return;
    const url = sanitizeUrl(this.shop.directions_url || '');
    if (!url) return;

    if (this.isAppCC) {
      dispatchChatMarianaLinks(url);
      return;
    }

    window.open(url, '_blank');
  };

  private renderChipSection(title: string, items: ShopAttribute[] | undefined, variant: 'services' | 'amenities') {
    if (!items?.length) return '';

    return html`
      <div class="inline-section">
        <h3 class="inline-section-title">${title}</h3>
        <div class="inline-chips">
          ${items.map(item => html`
            <div class="inline-chip">
              <div class="inline-chip-icon">
                ${item.image
        ? html`<img
                    src="${item.image}"
                    alt=""
                    class="inline-chip-icon-img ${variant === 'amenities' && isOriginalColorAmenity(item.name) ? 'inline-chip-icon-img--original-color' : ''}"
                  />`
        : html`<div class="inline-chip-icon-placeholder"></div>`}
              </div>
              <span class="inline-chip-label">${item.name || ''}</span>
            </div>
          `)}
        </div>
      </div>
    `;
  }

  private renderBrandsSection(brands: ShopAttribute[] | undefined) {
    const brandsWithLogo = getBrandsWithLogo(brands);
    if (!brandsWithLogo.length) return '';

    return html`
      <div class="inline-section">
        <h3 class="inline-section-title">Marcas</h3>
        <div class="inline-chips">
          ${brandsWithLogo.map(brand => html`
            <div class="inline-brand">
              <img src="${brand.image}" alt="${brand.name || ''}" class="inline-brand-logo" />
            </div>
          `)}
        </div>
      </div>
    `;
  }

  render() {
    const shop = this.shop;
    if (!shop) return html``;

    const image = getShopImage(shop);
    const hours = getFullScheduleLines(shop);
    const specialSchedule = shop.special_schedule?.trim();
    const distanceLabel = getDistanceLabel(shop);
    const postalAdminLine = getPostalAdminLine(shop);
    const directionsUrl = shop.directions_url;
    const hasAttributeSections = !!(
      shop.services?.length || shop.amenities?.length || getBrandsWithLogo(shop.brands).length
    );

    return html`
      <div class="inline-detail">
        <button class="inline-back" type="button" @click=${this.handleBackClick}>
          <div class="inline-back-icon"></div>
          Mais informações
        </button>

        <div class="inline-image-container">
          ${image ? html`
            <img
              src="${image}"
              alt="${shop.name || 'Loja'}"
              class="inline-image"
              @click=${this.handleImageClick}
              style="cursor: pointer;"
            />
          ` : html`
            <div class="inline-image-placeholder" @click=${this.handleImageClick} style="cursor: pointer;">
              <div class="inline-image-placeholder-icon"></div>
            </div>
          `}
          ${distanceLabel ? html`
            <div class="shop-distance-chip">
              <div class="shop-distance-icon"></div>
              <span class="shop-distance-text">${distanceLabel}</span>
            </div>
          ` : ''}
        </div>

        <div class="inline-header-info">
          <p class="inline-shop-name">${shop.name || 'Sem nome'}</p>
          <p class="inline-shop-address">${shop.street_line_1 || '-'}</p>
          ${postalAdminLine ? html`<p class="inline-shop-address-extra">${postalAdminLine}</p>` : ''}

          ${(hours.length > 0 || specialSchedule) ? html`
            <div class="shop-hours-row">
              <div class="shop-hours-icon"></div>
              <div class="shop-hours-lines">
                ${hours.map(line => html`<div class="shop-hours-line">${line}</div>`)}
                ${specialSchedule ? html`<div class="shop-hours-line shop-hours-line--special">${specialSchedule}</div>` : ''}
              </div>
              ${renderShopStatusTag(shop)}
            </div>
          ` : ''}
        </div>

        ${hasAttributeSections ? html`<div class="inline-divider"></div>` : ''}

        <div class="inline-footer-wrapper">
          ${hasAttributeSections ? html`
            <div class="inline-sections">
              ${this.renderChipSection('Serviços', shop.services, 'services')}
              ${this.renderChipSection('Comodidades', shop.amenities, 'amenities')}
              ${this.renderBrandsSection(shop.brands)}
            </div>
          ` : ''}

          ${directionsUrl ? html`
            <div class="inline-footer">
              <button class="inline-directions-cta" type="button" @click=${this.handleDirectionsClick}>
                <div class="inline-directions-icon"></div>
                Obter direções
              </button>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mariana-shop-detail-inline': MarianaShopDetailInline;
  }
}
